import { useEffect, useState, useContext, useCallback } from "react";
import styles from "./styles.module.css";
import toast from "react-hot-toast";
import { TextField } from "@mui/material";
import { roundOff, customerAmountFormat } from "helpers/NumberFormatHelper";
import ShopContext from "context/ShopContext";
import * as ShiftHelper from "helpers/ShiftHelper";
import * as SalesHelper from "helpers/SalesHelper";
import {
  PAYMENT_MODES,
  CASHBOOK_ENTRY_TYPE,
  INVOICE_ACTIONS,
  KEYPAD_BUTTONS,
  KEYBOARD_KEYS,
  EVENTS,
  SCANNER_RESTRICTED_INPUTS,
} from "const";
import { eventsEmitter } from "utils/events";
import CashIcon from "images/svg/cash.svg";
import GpayIcon from "images/svg/gpay.svg";
import PhonePayIcon from "images/svg/phonePay.svg";
import CardsIcon from "images/svg/card.svg";
import BackIcon from "images/svg/back.svg";
import CloseIcon from "images/svg/close-circle.svg";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import CheckoutActionMenu from "components/utils/CheckoutActionMenu/CheckoutActionMenu";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";
import * as TerminalHelper from "helpers/TermialHelper";

const paymentIcons = {
  [PAYMENT_MODES.CASH]: CashIcon,
  [PAYMENT_MODES.UPI]: GpayIcon,
  [PAYMENT_MODES.GPAY]: GpayIcon,
  [PAYMENT_MODES.PHONEPAY]: PhonePayIcon,
  [PAYMENT_MODES.CARD]: CardsIcon,
};

function AddPaymentToCustomer({ addPaymentToCustomer, currentBalance, customer, onClose }) {
  const { selectedShop, paymentModes: contextModes } = useContext(ShopContext);

  const [paymentModes, setPaymentModes] = useState([]);
  const [activeMode, setActiveMode] = useState(null);
  const [note, setNote] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const shiftObj = ShiftHelper.getCurrentShiftId();
  const shiftId = shiftObj?.id;

  const calculateTotal = useCallback((modes) => {
    const total = modes.reduce((acc, mode) => acc + (parseFloat(mode.amount) || 0), 0);
    setTotalAmount(total);
  }, []);

  const buildPaymentData = useCallback(
    (now) => {
      const tenders = paymentModes
        .filter((mode) => Number(mode.amount) > 0)
        .map((mode) => ({
          amount: Number(mode.amount),
          payment_mode: {
            id: mode.id,
            is_active: mode.is_enabled ?? true,
            name: mode.name,
            order: mode.order,
          },
        }));

      const payload = {
        credit: totalAmount,
        note,
        shift_id: shiftId,
        tenders,
      };

      const dbEntries = tenders.map((t) => ({
        shift_id: shiftId,
        local_id: Number(t.payment_mode.id),
        name: t.payment_mode.name,
        amount: t.amount,
        debit: 0,
        description: `${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT}: ${note}`,
        type: CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT,
        time: now,
        customer_name: customer?.name || "",
      }));

      const receiptData = {
        ...payload,
        customer: { ...customer, balance: Number(currentBalance) + Number(totalAmount) },
        tenders,
        time: now,
        amount: totalAmount,
      };
      return { payload, dbEntries, receiptData };
    },
    [paymentModes, totalAmount, note, shiftId, customer, currentBalance],
  );

  const executePostPaymentActions = useCallback(
    (receiptData, shouldPrint, shouldWhatsApp) => {
      if (shouldPrint) {
        SalesHelper.printCustomerPaymentReceipt({ payment: receiptData, shop: selectedShop });
      }
      if (shouldWhatsApp) {
        try {
          InvoiceActionHelper.shareCustomerPaymentOnWhatsApp(receiptData, selectedShop, customer);
        } catch (err) {
          toast.error(err?.message || "WhatsApp skip: Customer mobile missing.");
        }
      }
    },
    [selectedShop, customer],
  );

  const handleAddPayment = useCallback(
    async (options = {}) => {
      const { shouldPrint = true, shouldWhatsApp = false } = options;
      if (submitting) return;

      if (!note || totalAmount === 0) {
        toast.error("Please enter a note and a valid amount");
        return;
      }

      try {
        setSubmitting(true);
        const now = DateTimeHelpers.getLocalISOString();
        const { payload, dbEntries, receiptData } = buildPaymentData(now);

        await addPaymentToCustomer(payload);
        await Promise.all(dbEntries.map((entry) => ShiftHelper.addShiftPayment(entry)));
        executePostPaymentActions(receiptData, shouldPrint, shouldWhatsApp);

        toast.success("Payment added successfully");
        onClose();
      } catch (error) {
        console.error(error);
        toast.error("Failed to add payment");
        setSubmitting(false);
      }
    },
    [addPaymentToCustomer, buildPaymentData, executePostPaymentActions, note, totalAmount, submitting, onClose],
  );

  const onSaveInvoiceClick = useCallback(() => {
    handleAddPayment({ shouldPrint: false, shouldWhatsApp: false });
  }, [handleAddPayment]);

  const onDirectPrintClick = useCallback(() => {
    handleAddPayment({ shouldPrint: true, shouldWhatsApp: false });
  }, [handleAddPayment]);

  const onSaveAndWhatsAppClick = useCallback(() => {
    handleAddPayment({ shouldPrint: false, shouldWhatsApp: true });
  }, [handleAddPayment]);

  const onPrintAndWhatsAppClick = useCallback(() => {
    handleAddPayment({ shouldPrint: true, shouldWhatsApp: true });
  }, [handleAddPayment]);

  const onDefaultActionClick = useCallback(() => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const action = terminalSettings?.defaultInvoiceAction || INVOICE_ACTIONS.PRINT;
    switch (action) {
      case INVOICE_ACTIONS.SAVE:
        onSaveInvoiceClick();
        break;
      case INVOICE_ACTIONS.SAVE_AND_WHATSAPP:
        onSaveAndWhatsAppClick();
        break;
      case INVOICE_ACTIONS.PRINT_AND_WHATSAPP:
        onPrintAndWhatsAppClick();
        break;
      case INVOICE_ACTIONS.PRINT:
      default:
        onDirectPrintClick();
        break;
    }
  }, [onDirectPrintClick, onSaveAndWhatsAppClick, onSaveInvoiceClick, onPrintAndWhatsAppClick]);

  const calculateNewKeypadAmount = useCallback((currentAmount, val) => {
    const currentStr = (currentAmount ?? "0").toString();
    if (val === KEYPAD_BUTTONS.CLEAR || val === KEYPAD_BUTTONS.BACKSPACE) return "0";
    if (val === KEYPAD_BUTTONS.DELETE) {
      const sliced = currentStr.slice(0, -1);
      return sliced === "" || sliced === "-" ? "0" : sliced;
    }
    if (val === KEYPAD_BUTTONS.PLUS_MINUS) {
      if (currentStr.startsWith("-")) {
        return currentStr.slice(1) || "0";
      }
      return currentStr === "0" || currentStr === "" ? "-" : `-${currentStr}`;
    }
    if (typeof val === "string" && val.startsWith("+")) {
      const increment = Number(val.replace("+", "")) || 0;
      const num = (parseFloat(currentStr) || 0) + increment;
      return num.toString();
    }
    if (val === KEYPAD_BUTTONS.DOT || val === ".") {
      if (currentStr === "-") return "-0.";
      if (currentStr === "" || currentStr === "0") return "0.";
      return currentStr.includes(".") ? currentStr : `${currentStr}.`;
    }
    if (typeof val === "string" && !isNaN(val) && val.length === 1) {
      if (currentStr === "0") return val;
      if (currentStr === "-") return `-${val}`;
      return `${currentStr}${val}`;
    }
    return currentAmount;
  }, []);

  const handleKeypadInput = useCallback(
    (val) => {
      if (!val) return;
      setPaymentModes((prevModes) => {
        const updatedModes = prevModes.map((mode) =>
          mode.label === activeMode ? { ...mode, amount: calculateNewKeypadAmount(mode.amount, val) } : mode,
        );
        calculateTotal(updatedModes);
        return updatedModes;
      });
    },
    [activeMode, calculateNewKeypadAmount, calculateTotal],
  );

  const handleNoteChange = (event) => {
    setNote(event.target.value);
  };

  useEffect(() => {
    if (contextModes && contextModes.length > 0) {
      const modesWithAmounts = contextModes
        .filter(
          (m) =>
            m.name.toLowerCase() !== PAYMENT_MODES.CREDIT &&
            m.name.toLowerCase() !== PAYMENT_MODES.DISCOUNT &&
            m.name.toLowerCase() !== PAYMENT_MODES.POINTS,
        )
        .map((mode) => {
          const modeName = typeof mode === "string" ? mode : mode.name;
          return {
            ...mode,
            id: mode.id,
            label: modeName,
            amount: "0",
            icon: paymentIcons[(modeName || "").toLowerCase()] || null,
          };
        });

      setPaymentModes(modesWithAmounts);

      const cashMode = modesWithAmounts.find((m) => m.label.toLowerCase() === PAYMENT_MODES.CASH);
      setActiveMode(cashMode ? cashMode.label : modesWithAmounts[0]?.label);
    }
  }, [contextModes]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (submitting) return;

      const target = event.target;
      if (target && SCANNER_RESTRICTED_INPUTS.includes(target.tagName)) {
        return;
      }

      if (event.key === KEYBOARD_KEYS.ESCAPE) {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.F4) {
        event.preventDefault();
        onDefaultActionClick();
        return;
      }

      if (SalesHelper.handleArrowKeysNavigation(event, paymentModes, activeMode, (mode) => setActiveMode(mode.label))) {
        return;
      }

      SalesHelper.handleKeypadKeysInput(event, activeMode, handleKeypadInput);
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [submitting, onClose, onDefaultActionClick, paymentModes, activeMode, handleKeypadInput]);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headerSection}>
          <div className={styles.headerTitle}>
            <h2>Add Payment</h2>
            <h4>Log payment details to adjust the remaining balance.</h4>
          </div>
          {!submitting && (
            <img src={CloseIcon} alt="Close" onClick={onClose} style={{ cursor: "pointer", width: "24px" }} />
          )}
        </div>

        {submitting ? (
          <div style={{ padding: "20px" }}>
            <SkeletonLoading isOpen={true} count={4} height={60} xs={12} sm={12} md={12} lg={12} />
          </div>
        ) : (
          <>
            <div className={styles.descriptionSection}>
              <TextField
                placeholder="Payment Description"
                size="small"
                fullWidth
                value={note}
                onChange={handleNoteChange}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    height: "64px",
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: "#CDCDCF",
                    },
                    "&:hover fieldset": {
                      borderColor: "#CDCDCF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#7C7C7F",
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </div>

            <div className={styles.paymentSection}>
              <div className={styles.paymentTabs}>
                {paymentModes.map((method, index) => (
                  <button
                    key={method.label}
                    onClick={() => setActiveMode(method.label)}
                    className={`${index === 0 ? styles.firstBtn : ""} ${
                      index === paymentModes.length - 1 ? styles.lastBtn : ""
                    } ${method.label === activeMode ? styles.activeTab : ""}`}
                  >
                    <div className={styles.paymentHead}>
                      {method.icon && <img src={method.icon} alt={method.label} className={styles.paymentIcon} />}
                      <h3 className={styles.label}>{method.label}</h3>
                    </div>

                    <h2 className={styles.amount}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {roundOff(parseFloat(method.amount) || 0)}
                    </h2>
                  </button>
                ))}
              </div>

              <div className={styles.keypad}>
                <button
                  className={styles.keyBtn}
                  style={{ borderTopLeftRadius: "20px" }}
                  onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ONE)}
                >
                  1
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.TWO)}>
                  2
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.THREE)}>
                  3
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TEN)}>
                  +10
                </button>
                <button
                  className={styles.keyBtn}
                  style={{ borderTopRightRadius: "20px" }}
                  onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_HUNDERD)}
                >
                  +100
                </button>

                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FOUR)}>
                  4
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.FIVE)}>
                  5
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SIX)}>
                  6
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIFTY)}>
                  +50
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_TWO_HUNDERD)}>
                  +200
                </button>

                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.SEVEN)}>
                  7
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.EIGHT)}>
                  8
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.NINE)}>
                  9
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_MINUS)}>
                  +/-
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_FIVE_HUNDRED)}>
                  +500
                </button>

                <button
                  className={styles.keyBtn}
                  style={{ borderBottomLeftRadius: "20px", backgroundColor: "rgba(226, 225, 228, 1)" }}
                  onClick={() => handleKeypadInput(KEYPAD_BUTTONS.BACKSPACE)}
                >
                  <img src={BackIcon} alt="Back" />
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.ZERO)}>
                  0
                </button>
                <button className={styles.keyBtn} onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DOT)}>
                  .
                </button>
                <button
                  className={styles.keyBtn}
                  onClick={() => handleKeypadInput(KEYPAD_BUTTONS.DELETE)}
                  style={{ backgroundColor: "rgba(226, 225, 228, 1)" }}
                >
                  Delete
                </button>
                <button
                  className={styles.keyBtn}
                  style={{ borderBottomRightRadius: "20px" }}
                  onClick={() => handleKeypadInput(KEYPAD_BUTTONS.PLUS_ONE_THOUSAND)}
                >
                  +1000
                </button>
              </div>

              <div className={styles.footer}>
                <div className={styles.footerInfo}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Balance :</span>
                    <span className={styles.infoValue}>{customerAmountFormat(currentBalance)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Total Amount :</span>
                    <span className={styles.infoValue}>{customerAmountFormat(totalAmount)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Final Balance :</span>
                    <span className={styles.infoValue}>
                      {customerAmountFormat(Number(currentBalance) + Number(totalAmount))}
                    </span>
                  </div>
                </div>
                <div className={styles.footerBtns}>
                  <button className={styles.closeBtn} onClick={onClose}>
                    Cancel
                  </button>
                  <CheckoutActionMenu
                    onDefaultActionClick={onDefaultActionClick}
                    onPrintClick={onDirectPrintClick}
                    onSaveInvoice={onSaveInvoiceClick}
                    onSaveAndWhatsApp={onSaveAndWhatsAppClick}
                    onPrintAndWhatsApp={onPrintAndWhatsAppClick}
                    isLoading={submitting}
                    buttonLabel={InvoiceActionHelper.getInvoiceActionButtonLabel()}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AddPaymentToCustomer;
